/** tm/components/ReportSendModal.tsx — 리포트 발송 미리보기 · 발송
 *
 *  실제로 나갈 문자를 수신자별로 그대로 보여주고, 확인한 문장을 그대로 보낸다.
 *  서버는 여기서 받은 문장을 다시 만들지 않는다 — 미리보기와 다른 문자가 나갈 수 없다.
 *
 *  문자는 회수가 안 되므로 안전장치를 셋 둔다.
 *    ① 수신자별 체크 — 기본값은 연락처가 있는 사람만 켜짐
 *    ② 같은 버전을 이미 보냈으면 상단에 경고
 *    ③ [보내기] 를 눌러도 바로 나가지 않고 마지막 확인을 한 번 더 받는다
 */
import { useState, useEffect, useCallback } from 'react';
import { C, T, R } from './ui';
import * as rapi from '../api-report';
import type { DispatchItem, DispatchPreview } from '../api-report';

interface Props {
  consultId: string;
  onClose: () => void;
  onSent?: () => void;
  onToast: (msg: string, type?: string) => void;
}

const LMS_LIMIT = 90;   // EUC-KR 90바이트를 넘으면 LMS 로 나간다

const KIND_LABEL: Record<string, string> = { fp: '담당 FP', wm: 'WM' };   // WM 은 여러 분(2026-10-26)

/** 서버와 같은 방식으로 센다 — 화면에서 문구를 고쳐도 즉시 반영되게 */
function smsBytes(s: string): number {
  let n = 0;
  for (const ch of String(s || '')) n += ch.charCodeAt(0) > 127 ? 2 : 1;
  return n;
}

export default function ReportSendModal({ consultId, onClose, onSent, onToast }: Props) {
  const [preview, setPreview] = useState<DispatchPreview | null>(null);
  const [rows, setRows] = useState<DispatchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = await rapi.getDispatchPreview(consultId);
      setPreview(p);
      setRows(p.items || []);
    } catch (e) {
      onToast(e instanceof Error ? e.message : '미리보기 실패', 'error');
      onClose();
    } finally { setLoading(false); }
  }, [consultId, onClose, onToast]);

  useEffect(() => { load(); }, [load]);

  function update(idx: number, patch: Partial<DispatchItem>) {
    setRows(prev => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  const chosen = rows.filter(r => r.selected && !r.blocked && r.message.trim());

  async function doSend() {
    if (chosen.length === 0) { onToast('보낼 대상을 선택해 주세요', 'error'); return; }
    setSending(true);
    try {
      const r = await rapi.sendDispatch(consultId, chosen.map(x => ({
        kind: x.kind, name: x.name, phone: x.phone, message: x.message,
      })));
      const okCount = r.results.filter(x => x.ok).length;
      const failed = r.results.filter(x => !x.ok);
      if (okCount > 0) onToast(`${okCount}건 발송 완료`);
      if (failed.length > 0) {
        onToast(`발송 실패 ${failed.length}건 — ${failed.map(f => `${f.name}(${f.error || '원인 미상'})`).join(', ')}`, 'error');
      }
      if (onSent) onSent();
      onClose();
    } catch (e) {
      onToast(e instanceof Error ? e.message : '발송 실패', 'error');
      setConfirming(false);
    } finally { setSending(false); }
  }

  const btn = (kind: 'primary' | 'ghost' | 'danger') => ({
    padding: '9px 18px', borderRadius: R.sm, fontSize: T.sm, fontWeight: 700, cursor: 'pointer',
    border: `1px solid ${kind === 'ghost' ? C.border : C.accent}`,
    background: kind === 'ghost' ? C.white : C.accent,
    color: kind === 'ghost' ? C.sub : '#fff',
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={sending ? undefined : onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.55)', backdropFilter: 'blur(2px)' }} />

      <div style={{ position: 'relative', width: '100%', maxWidth: 620, maxHeight: '90vh', background: C.white, borderRadius: 12, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>

        <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #111827, #1B3A5C)', borderRadius: '12px 12px 0 0' }}>
          <div style={{ fontSize: T.md, fontWeight: 700, color: '#fff' }}>📤 리포트 발송</div>
          <div style={{ fontSize: T.xs, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
            {preview?.companyName ? `${preview.companyName} · ` : ''}아래 내용 그대로 나갑니다
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {loading && <div style={{ padding: '30px 0', textAlign: 'center', fontSize: T.xs, color: C.muted }}>불러오는 중…</div>}

          {!loading && preview && !preview.ready && (
            <div style={{ padding: '20px 0', textAlign: 'center', fontSize: T.sm, color: C.orange }}>
              {preview.reason || '발송할 수 없습니다'}
            </div>
          )}

          {!loading && preview?.alreadySent && preview.alreadySent.version === preview.version && (
            <div style={{ fontSize: T.xs, color: C.orange, background: C.orangeLt, padding: '9px 12px', borderRadius: R.sm, marginBottom: 12, lineHeight: 1.7 }}>
              ⚠ v{preview.alreadySent.version} 은 이미 보냈습니다
              {preview.alreadySent.at ? ` (${String(preview.alreadySent.at).slice(0, 16).replace('T', ' ')})` : ''}.
              <br />같은 내용이 한 번 더 갑니다. 정말 다시 보내실 건가요?
            </div>
          )}

          {!loading && rows.map((r, i) => {
            const bytes = smsBytes(r.message);
            const isLms = bytes > LMS_LIMIT;
            return (
              <div key={r.key || `${r.kind}:${i}`} style={{ border: `1px solid ${r.selected && !r.blocked ? C.accent : C.border}`, borderRadius: R.sm, padding: 12, marginBottom: 12, background: r.blocked ? C.bg : C.white }}>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input type="checkbox" checked={r.selected && !r.blocked} disabled={!!r.blocked}
                    onChange={e => update(i, { selected: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: r.blocked ? 'default' : 'pointer' }} />
                  <span style={{ fontSize: T.sm, fontWeight: 700 }}>{KIND_LABEL[r.kind] || r.kind}</span>
                  <span style={{ fontSize: T.sm }}>{r.name}</span>
                  <span style={{ fontSize: T.xs, color: C.muted }}>{r.phone || '연락처 없음'}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ fontSize: 10, fontWeight: 700, color: isLms ? C.accent : C.muted, background: isLms ? C.accentLt : C.bg, padding: '2px 8px', borderRadius: 10 }}>
                    {isLms ? 'LMS' : 'SMS'} {bytes}byte
                  </span>
                </div>

                {r.blocked ? (
                  <div style={{ fontSize: T.xs, color: C.red }}>{r.blocked} — 이 사람에게는 보낼 수 없습니다</div>
                ) : (
                  <textarea value={r.message} onChange={e => update(i, { message: e.target.value })}
                    style={{ width: '100%', minHeight: 150, padding: '10px 12px', borderRadius: R.sm, border: `1px solid ${C.border}`,
                      fontFamily: 'inherit', fontSize: T.xs, lineHeight: 1.7, outline: 'none', resize: 'vertical' as const, boxSizing: 'border-box' as const }} />
                )}
              </div>
            );
          })}

          {!loading && rows.length > 0 && (
            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
              링크는 짧은 주소로 바뀌어 나갑니다. 문구를 고치셔도 링크는 그대로 두세요 —
              지우면 받는 분이 리포트를 열 수 없습니다.
            </div>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 8, background: C.bg, borderRadius: '0 0 12px 12px' }}>
          {!confirming ? (
            <>
              <button onClick={onClose} disabled={sending} style={btn('ghost')}>닫기</button>
              <span style={{ flex: 1, fontSize: T.xs, color: C.muted }}>
                {chosen.length > 0 ? `${chosen.length}명에게 보냅니다` : '보낼 대상이 없습니다'}
              </span>
              <button onClick={() => setConfirming(true)} disabled={chosen.length === 0 || sending} style={{ ...btn('primary'), opacity: chosen.length === 0 ? 0.5 : 1 }}>
                📤 보내기
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setConfirming(false)} disabled={sending} style={btn('ghost')}>취소</button>
              <span style={{ flex: 1, fontSize: T.xs, color: C.red, fontWeight: 600 }}>
                {chosen.map(x => x.name).join(', ')} — 지금 바로 나갑니다. 되돌릴 수 없습니다.
              </span>
              <button onClick={doSend} disabled={sending} style={btn('primary')}>
                {sending ? '보내는 중…' : '네, 보냅니다'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
